import React from 'react';
import styled from 'styled-components';

import { isDefined } from '../../../../../../../../utils/variables';
import { getDefaultEnumValueColor } from '../../../../../../../../viewer/elements/design/colors';
import { RevertChange } from '../../../../../../../../viewer/elements/design/design-node/design-node';
import {
  AttributeData,
  AttributeGroupEntry,
  AttributeId,
  SkyMapAttribute,
  SkyMapExtrasUnitType,
  ValueId,
} from '../../../../../../../../viewer/elements/design/skymap-extras';
import { publish, SubscriptionTopic } from '../../../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../../../viewer/tools/design-tool';
import { addAndCommitAction } from '../../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/actions';
import {
  ClearAttribute,
  getMatchingSelection,
  setAttributesOnSelection,
} from '../../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/attributes';
import { deleteSelection } from '../../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/delete-selection';
import { useNonce } from '../../../../../../../hooks/use-nonce';
import { EnumValues } from './enum-values';
import { UnitValues } from './unit-values';

type Props = {
  tool: DesignTool;
  attribute: SkyMapAttribute;
};

export const EditAttribute = (props: Props) => {
  const enumAttribute = props.attribute.type === 'enum' ? props.attribute : undefined;
  const [unit, setUnit] = React.useState<SkyMapExtrasUnitType | string | undefined>(
    props.attribute.type === 'number' ? props.attribute.unit : undefined,
  );

  const [nonce, setNonce] = useNonce();

  if (props.attribute.type === 'string') {
    return null;
  }

  return (
    <Content>
      {enumAttribute ? (
        <EnumValues
          colorMap={enumAttribute.colorMap}
          nonce={nonce}
          valueMap={enumAttribute.valueMap}
          onColorUpdated={(valueIndex, newColor) => {
            const oldColor = enumAttribute.colorMap.get(valueIndex);

            addAndCommitAction(props.tool, {
              commit: () => {
                enumAttribute.colorMap.set(valueIndex, newColor);
                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
              rollback: () => {
                if (isDefined(oldColor)) {
                  enumAttribute.colorMap.set(valueIndex, oldColor);
                } else {
                  enumAttribute.colorMap.delete(valueIndex);
                }

                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
            });

            props.tool.asset.designNode.refresh();
            setNonce();
          }}
          onRename={(valueIndex, newName) => {
            const oldName = enumAttribute.valueMap.get(valueIndex);

            addAndCommitAction(props.tool, {
              commit: () => {
                enumAttribute.valueMap.set(valueIndex, newName);
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
              rollback: () => {
                if (isDefined(oldName)) {
                  enumAttribute.valueMap.set(valueIndex, oldName);
                } else {
                  enumAttribute.valueMap.delete(valueIndex);
                }

                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
            });

            setNonce();
          }}
          onValueAdded={(id, value) => {
            let changes: RevertChange[] = [];

            addAndCommitAction(props.tool, {
              commit: () => {
                enumAttribute.valueMap.set(id, value);
                enumAttribute.colorMap.set(id, getDefaultEnumValueColor(id));

                changes = [
                  {
                    type: 'custom',
                    callback: () => {
                      enumAttribute.valueMap.delete(id);
                      enumAttribute.colorMap.delete(id);
                    },
                  },
                ];

                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
                setNonce();
              },
              rollback: () => {
                props.tool.asset.designNode.revert(changes);
                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
                setNonce();
              },
            });
          }}
          onValueRemoved={(valueIdToRemove, action) => {
            let changes: RevertChange[] = [];

            const newAttributes = new Map<AttributeId, AttributeData & ClearAttribute>();

            const newAttributeId =
              action?.action === 'update' && isDefined(action.newValueId)
                ? action.newValueId
                : undefined;

            newAttributes.set(
              enumAttribute.id,
              isDefined(newAttributeId)
                ? {
                    type: 'enum',
                    index: newAttributeId,
                  }
                : {
                    type: 'enum',
                    index: valueIdToRemove,
                    clearAttribute: true,
                  },
            );

            const selection = getMatchingSelection(props.tool.asset.designNode, [
              { id: enumAttribute.id, index: valueIdToRemove, type: 'enum' },
            ]);

            addAndCommitAction(props.tool, {
              commit: () => {
                if (action?.action === 'delete') {
                  changes = deleteSelection(props.tool.asset.designNode, selection);
                } else {
                  // Update affected geometry.
                  changes = setAttributesOnSelection(props.tool, selection, newAttributes);

                  // We only need to remove the entries from the attribute groups if the we are not setting a new value
                  if (!isDefined(newAttributeId)) {
                    removeAffectedEntriesFromAttributeGroups(
                      props.tool,
                      enumAttribute,
                      valueIdToRemove,
                      changes,
                    );
                  }
                }

                removeEnumValue(enumAttribute, changes, valueIdToRemove);

                // Clear active attribute group and active attributes from tool
                // in case the deleted data conflicts with what was removed.
                if (
                  isDefined(props.tool.activeAttributeGroup) ||
                  isDefined(props.tool.activeAttributeGroupRowId)
                ) {
                  props.tool.activeAttributeGroup = undefined;
                  props.tool.activeAttributeGroupRowId = undefined;
                  props.tool.activeAttributes.clear();
                }

                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
                setNonce();
              },
              rollback: () => {
                props.tool.asset.designNode.revert(changes);
                props.tool.asset.designNode.refresh();
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
                setNonce();
              },
            });
          }}
        />
      ) : (
        <UnitValues
          setUnit={(newValue) => {
            setUnit(newValue);

            const attribute = props.attribute.type === 'number' ? props.attribute : undefined;
            if (!isDefined(attribute)) {
              return;
            }

            const oldValue = attribute.unit;
            let changes: RevertChange[] = [];

            addAndCommitAction(props.tool, {
              commit: () => {
                attribute.unit = newValue;
                setUnit(newValue);

                changes = [
                  {
                    type: 'custom',
                    callback: () => {
                      attribute.unit = oldValue;
                      setUnit(oldValue);
                      publish(SubscriptionTopic.DesignAttributesChanged, undefined);
                    },
                  },
                ];

                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
              rollback: () => {
                props.tool.asset.designNode.revert(changes);
                publish(SubscriptionTopic.DesignAttributesChanged, undefined);
              },
            });
          }}
          unit={unit}
        />
      )}
    </Content>
  );
};

const Content = styled.div`
  padding: 0.4em;
`;

function removeEnumValue(
  enumAttribute: Extract<SkyMapAttribute, { type: 'enum' }>,
  changes: RevertChange[],
  valueIdToRemove: ValueId,
) {
  const oldValue = enumAttribute.valueMap.get(valueIdToRemove);
  const oldColor = enumAttribute.colorMap.get(valueIdToRemove);
  enumAttribute.valueMap.delete(valueIdToRemove);
  enumAttribute.colorMap.delete(valueIdToRemove);
  changes.unshift({
    type: 'custom',
    callback: () => {
      if (isDefined(oldValue)) {
        enumAttribute.valueMap.set(valueIdToRemove, oldValue);
      }
      if (isDefined(oldColor)) {
        enumAttribute.colorMap.set(valueIdToRemove, oldColor);
      }
    },
  });
}

function removeAffectedEntriesFromAttributeGroups(
  tool: DesignTool,
  enumAttribute: SkyMapAttribute,
  valueIdToRemove: ValueId,
  changes: RevertChange[],
) {
  const itemsToRemove: {
    array: AttributeGroupEntry[];
    items: { index: number; data: AttributeData }[];
  }[] = [];

  // Extract affected entry rows.
  for (const group of tool.asset.designNode.getAttributeGroups()) {
    const items: { index: number; data: AttributeData }[] = [];
    for (let i = 0; i < group.entries.length; i++) {
      const data = group.entries[i].data.get(enumAttribute.id);

      if (data?.type === 'enum' && data.index === valueIdToRemove) {
        items.push({
          index: i,
          data,
        });
      }
    }

    itemsToRemove.push({ array: group.entries, items });
  }

  // Remove the affeted entries.
  for (const item of itemsToRemove) {
    for (let i = item.items.length - 1; i >= 0; i--) {
      const removedElement = item.array.splice(item.items[i].index, 1);

      changes.unshift({
        type: 'custom',
        callback: () => {
          item.array.splice(item.items[i].index, 0, ...removedElement);
        },
      });
    }
  }
}
